import { useCancelRequestHook } from '@op/hooks/useCancelRequest';
import {
  DEFAULT_REPORT_OWNER,
  type TReportOwner,
  useReportPermissionNotify,
} from '@op/components/ReportPermission';
import {
  addRepeatSummaryTask,
  getAIAnalysisSummaryList,
  getRepeatSummaryTaskList,
  getReportMenuTab,
  updateRepeatSummaryTask,
  pauseKeywordAnalysisTask,
  resumeKeywordAnalysisTask,
  pauseCustomAITask,
} from '@op/services';
import React, { useEffect, useMemo, useState } from 'react';
import { Button, Modal, notification } from 'tea-component';
import { aigcSaveAiAnalysis } from '@op/services/aigc';
import IconSaveSuccess from '@op/assets/svg/icon-save-success.svg';
import SaveFloatButton from '../../KeywordAndURLAnalysis/component/SaveFloatButton';
import SvgIcon from '@home/components/SvgIcon';
import { asyncCreateShortUrl } from '@op/components/FilterCommon/utils/historyUtils';
import { idKeyNameMap } from '@op/constants/page';
import { saveIcon, saveSuccessIcon } from '@op/constants/chart';
import { useGetSingePageSourceRule } from '@op/hooks/useGetPageSourceRule';
import { SortBy } from 'tea-component/lib/table/addons';
import { usePageInfo } from '@op/hooks/common';
import useLanguage from '@op/hooks/useLanguage';
import { useDeleteSummaryItem } from '../../hooks';

export function useAIAnalysisSummaryList({ filter, needRequest }: any) {
  const [data, setData] = useState<any>([]);
  const [total, setTotal] = useState<any>(0);
  const [error, setError] = useState(false);
  const { loading, wrapperRequestTile } = useCancelRequestHook();
  function requestSummaryList(params = filter) {
    if (!params.edition_unified_id) return;
    setError(false);
    setData([]);
    wrapperRequestTile(getAIAnalysisSummaryList, params)
      .then((data) => {
        const { total, list } = data || {};
        const records = (list || []).map((item: any) => ({
          ...item,
          name: item.save_name,
          id: item.save_id,
        }));
        setData(records);
        setTotal(total || 0);
      })
      .catch(() => {
        setError(true);
      });
  }

  const { deleteCommentSummary } = useDeleteSummaryItem({
    finallyCallback: requestSummaryList,
  });

  useEffect(() => {
    if (needRequest) {
      requestSummaryList();
    }
  }, [filter, needRequest]);

  return {
    data,
    total,
    loading,
    error,
    requestSummaryList,
    deleteSummary: deleteCommentSummary,
  };
}

export function useRepeatSummaryList({
  filter,
  clearPageInfo,
  needRequest,
  pageLoaded,
  requestFunc = getRepeatSummaryTaskList,
  isKeywordTopic = false,
  type
}: any) {
  const [data, setData] = useState<any>([]);
  const [total, setTotal] = useState<any>(0);
  const [error, setError] = useState(false);
  const { loading, wrapperRequestTile } = useCancelRequestHook();
  const { formatMessage } = useLanguage();

  const { Update_Repeat_Report } = useGetSingePageSourceRule({
    keys: ['Update_Repeat_Report'],
  });

  function requestSummaryList(params = filter) {
    setError(false);
    setData([]);
    wrapperRequestTile(requestFunc, params)
      .then((data) => {
        const { total, list } = data || {};
        const records = (list || []).map((item: any) => ({
          ...item,
          is_creator: isKeywordTopic || !!!Update_Repeat_Report,
        }));
        setData(records);
        setTotal(total || 0);
      })
      .catch(() => {
        setError(true);
      })
      .finally(() => {
        pageLoaded && (pageLoaded.current = true);
      });
  }

  const { deleteSubscriptionSummary, deleteKeywordTopicSubscriptionSummary } =
    useDeleteSummaryItem({
      thenCallback: clearPageInfo,
    });

  /**
   * 暂停关键词和话题分析例行任务
   * @param record - { task_id?: string; id: string; name: string }
   */
  async function stopSubscriptionSummary(record: {
    task_id?: string;
    id: string;
    name: string;
    gameInfo: any;
  }) {
    if (!isKeywordTopic) return false;
    const { name, id: taskId, gameInfo } = record;
    if (!taskId) return false;

    const yes = await Modal.confirm({
      message: formatMessage({ id: 'Stop Repeat Task?' }),
      description: formatMessage(
        {
          id: 'Stop {name}? This subscription will be canceled and cannot be undone. Please proceed with caution.',
        },
        { name: <b>{name}</b> },
      ),
      okText: formatMessage({ id: 'Confirm' }),
      cancelText: formatMessage({ id: 'Cancel' }),
    });

    if (yes) {

      if (type === 'repeat_summary_task') {
        pauseCustomAITask({
          edition_unified_id: gameInfo.edition_unified_id,
          entity_type: gameInfo.entity_type,
          id_type: gameInfo.id_type,
          id: taskId,
        })
          .then((data: any) => {
            const { is_paused: pausedTask } = data || {};
            if (pausedTask) {
              requestSummaryList();
              notification.success({
                description: formatMessage({ id: 'Stop success' }),
              });
            } else {
              notification.error({
                description: formatMessage({ id: 'Stop failed' }),
              });
            }
          })
          .catch(() => {
            notification.error({
              description: formatMessage({ id: 'Stop failed' }),
            });
          });
      } else {
        pauseKeywordAnalysisTask({
          ...gameInfo,
          task_id: taskId,
        })
          .then(() => {
            clearPageInfo?.();
            notification.success({
              description: formatMessage({ id: 'Stop success' }),
            });
          })
          .catch(() => {
            notification.error({
              description: formatMessage({ id: 'Stop failed' }),
            });
          });
      }


    }
  }

  /**
   * 恢复关键词和话题分析例行任务
   * @param record - { task_id?: string; id: string; name: string }
   */
  async function resumeSubscriptionSummary(record: {
    task_id?: string;
    id: string;
    name: string;
    gameInfo: any;
  }) {
    if (!isKeywordTopic) return false;
    const { name, id: taskId, gameInfo } = record;
    if (!taskId) return false;


    const yes = await Modal.confirm({
      message: formatMessage({ id: 'Resume Repeat Task?' }),
      description: formatMessage(
        {
          id: 'Resume {name}? This will continue generating new reports for this task.',
        },
        { name: <b>{name}</b> },
      ),
      okText: formatMessage({ id: 'Confirm' }),
      cancelText: formatMessage({ id: 'Cancel' }),
    });

    if (yes) {
      resumeKeywordAnalysisTask({
        ...gameInfo,
        task_id: taskId,
      })
        .then(() => {
          clearPageInfo?.();
          notification.success({
            description: formatMessage({ id: 'Resume success' }),
          });
        })
        .catch(() => {
          notification.error({
            description: formatMessage({ id: 'Resume failed' }),
          });
        });
    }
  }

  useEffect(() => {
    if (needRequest) {
      requestSummaryList();
    }
  }, [filter, needRequest]);

  return {
    data,
    total,
    loading,
    error,
    requestSummaryList,
    deleteSummary: isKeywordTopic
      ? deleteKeywordTopicSubscriptionSummary
      : deleteSubscriptionSummary,
    stopSummary: stopSubscriptionSummary,
    resumeSummary: resumeSubscriptionSummary,
  };
}

export function useAiReportSummaryList({
  gameInfo,
  isOneTime = true,
  needRequest,
  owner = DEFAULT_REPORT_OWNER,
}: {
  gameInfo: any;
  isOneTime: boolean;
  needRequest: boolean;
  owner?: TReportOwner;
}) {
  const { formatMessage } = useLanguage();
  const [sortBy, setSortBy] = useState<SortBy<any>>({
    by: 'create_time',
    order: 'desc',
  });

  const { pageInfo, changePageInfo } = usePageInfo({
    defaultPageInfo: { page: 1, page_size: 10 },
  });

  const params = useMemo(
    () => ({
      ...gameInfo,
      ...pageInfo,
      sort_item: sortBy.by,
      sort_model: sortBy.order,
      owner,
    }),
    [gameInfo, pageInfo, sortBy, owner],
  );

  const repeatSummaryList = useRepeatSummaryList({
    filter: {},
    clearPageInfo: changePageInfo,
    needRequest: false,
  });

  const aiAnalysisSummaryList = useAIAnalysisSummaryList({
    filter: params,
    clearPageInfo: changePageInfo,
    needRequest: false,
  });

  useEffect(() => {
    const item = isOneTime ? aiAnalysisSummaryList : repeatSummaryList;

    item.requestSummaryList(params);
  }, [params, isOneTime, needRequest]);

  const { records, total } = useMemo(() => {
    const { data, total } = isOneTime
      ? aiAnalysisSummaryList
      : repeatSummaryList;
    return {
      records: data || [],
      total: total || 0,
    };
  }, [aiAnalysisSummaryList, repeatSummaryList, isOneTime]);

  /**
 * 暂停关键词和话题分析例行任务
 * @param record - { task_id?: string; id: string; name: string }
 */
  async function stopSubscriptionSummary(record: {
    task_id?: string;
    id: string;
    name: string;
    gameInfo: any;
  }) {
    const { name, id, gameInfo } = record;
    if (!id) return false;

    const yes = await Modal.confirm({
      message: formatMessage({ id: 'Stop Repeat Task?' }),
      description: formatMessage(
        {
          id: 'Stop {name}? This subscription will be canceled and cannot be undone. Please proceed with caution.',
        },
        { name: <b>{name}</b> },
      ),
      okText: formatMessage({ id: 'Confirm' }),
      cancelText: formatMessage({ id: 'Cancel' }),
    });

    if (yes) {
      pauseCustomAITask({
        ...gameInfo,
        id,
      })
        .then((data: any) => {
          const { is_paused: pausedTask } = data || {};
          if (pausedTask) {
            notification.success({
              description: formatMessage({ id: 'Stop success' }),
            });
            const item = isOneTime ? aiAnalysisSummaryList : repeatSummaryList;
            item.requestSummaryList(params);
          } else {
            notification.error({
              description: formatMessage({ id: 'Stop failed' }),
            });
          }
        })
        .catch(() => {
          notification.error({
            description: formatMessage({ id: 'Stop failed' }),
          });
        });
    }
  }

  return {
    ...(isOneTime ? aiAnalysisSummaryList : repeatSummaryList),
    sortBy,
    setSortBy,
    pageInfo,
    changePageInfo,
    records,
    total,
    params,
    stopSummary: stopSubscriptionSummary
  };
}

export function useSaveCustomSummaryReport({
  gameInfo,
  changeTab,
}: {
  gameInfo: any;
  changeTab: any;
}) {
  const { formatMessage } = useLanguage();
  const permissionNotify = useReportPermissionNotify();
  const [saveVisible, setSaveVisible] = useState<boolean>(false);
  const [saveParams, setSaveParams] = useState<any>(null);
  const [oriFilter, setOriFilter] = useState<any>(null);
  const [isSave, setIsSave] = useState<boolean>(false);

  async function saveUserSummary(name: string, otherParams = {}) {
    // 没有名称并且有保存id就不保存
    if (!name || !oriFilter || !saveParams) return false;

    const shortUrl = await asyncCreateShortUrl({
      filter: oriFilter,
      gameInfo,
      updateUrl: false,
      errorText: formatMessage({ id: 'Analysis save failed' }),
    });

    if (!shortUrl) return;
    return aigcSaveAiAnalysis({
      ...saveParams,
      name,
      ...otherParams,
      short_url: shortUrl,
    })
      .then((data) => {
        permissionNotify?.setPendingReportTarget(data);
        setIsSave(true);
        notification.success({
          description: formatMessage({ id: 'Successfully saved analysis' }),
        });
        return true;
      })
      .catch(() => {
        notification.error({
          description: formatMessage({ id: 'Analysis save failed' }),
        });
        return false;
      });
  }

  function toggleSaveWindow() {
    setSaveVisible((flag) => !flag);
  }

  const { saveTipsElement, saveTipsIcon, cardSaveIcon } = useMemo(() => {
    if (isSave) {
      const text = formatMessage(
        { id: 'This analysis report has been saved to the {page}' },
        {
          page: (
            <Button
              style={{ marginLeft: 5, width: 'unset' }}
              type="link"
              onClick={() => changeTab?.()}

            >
              {formatMessage({ id: '[Summary List]' })}
            </Button>
          ),
        },
      );
      const element = (
        <div className="dbo-save-tips">
          <span className="dbo-save-tips-icon">
            <img src={IconSaveSuccess} />
          </span>
          {text}
        </div>
      );
      return {
        saveTipsElement: element,
        saveTipsIcon: (
          <SaveFloatButton
            success={true}
            failText={''}
            successText={text}
          ></SaveFloatButton>
        ),
        cardSaveIcon: { ...saveSuccessIcon, tip: text, placement: 'right' },
      };
    }

    const text = formatMessage({ id: 'Save to Summary list and view later' });
    return {
      saveTipsElement: (
        <Button
          type="link"
          className="save-analysis"
          onClick={() => toggleSaveWindow()}
        >
          <SvgIcon type="datalab-save" className="save-icon" />
          {text}
        </Button>
      ),
      saveTipsIcon: (
        <SaveFloatButton
          success={false}
          handleClick={toggleSaveWindow}
          successText={undefined}
          failText={text}
        ></SaveFloatButton>
      ),
      cardSaveIcon: {
        ...saveIcon,
        tip: formatMessage({ id: 'Save to Analysis list and view later' }),
        placement: 'right',
      },
    };
  }, [isSave, formatMessage]);

  return {
    saveVisible,
    saveParams,
    setSaveParams,
    saveUserSummary,
    saveTipsElement,
    saveTipsIcon,
    toggleSaveWindow,
    isSave,
    setIsSave,
    setOriFilter,
    cardSaveIcon,
  };
}
export function useCreateRepeatSummaryTask({
  filter,
  gameInfo,
  repeatFilter,
  toggleVisible,
  formatMessage,
  request,
  oriFilter,
  promptInfo,
}: any) {
  const permissionNotify = useReportPermissionNotify();

  async function createTask() {
    const shortId = await asyncCreateShortUrl({
      filter: {
        ...oriFilter,
        promptInfo: {
          ...promptInfo,
          gptOptions: promptInfo?.task_type,
          modelType: promptInfo?.model_type,
        },
      },
      gameInfo,
      updateUrl: false,
      errorText: formatMessage({ id: 'Create failed' }),
    });
    if (!shortId) return;
    let isSuccess = false;
    addRepeatSummaryTask({
      ...gameInfo,
      ...filter,
      ...repeatFilter,
      ...promptInfo,
      short_url: shortId,
    })
      .then((data) => {
        permissionNotify?.setPendingReportTarget(data);
        notification.success({
          description: formatMessage({ id: 'Create success' }),
        });
        toggleVisible();
        isSuccess = true;
      })
      .catch(() => {
        notification.error({
          description: formatMessage({ id: 'Create failed' }),
        });
      })
      .finally(() => {
        if (isSuccess) {
          request?.();
        }
      });
  }

  function updateTask() {
    let isSuccess = false;
    updateRepeatSummaryTask({
      cycle_end_date: repeatFilter.cycle_end_date,
      name: repeatFilter.name,
      name_cn: repeatFilter.name_cn,
      status: 1,
      id: repeatFilter.id,
    })
      .then(() => {
        notification.success({
          description: formatMessage({ id: 'Edit success' }),
        });
        toggleVisible();
        isSuccess = true;
      })
      .catch(() => {
        notification.error({
          description: formatMessage({ id: 'Edit failed' }),
        });
      })
      .finally(() => {
        if (isSuccess) {
          request?.();
        }
      });
  }

  return { createTask, updateTask };
}

export function useAIInsightsReport({
  gameInfo,
  menuId,
  pageLoaded,
  sourceType,
}: {
  gameInfo: any;
  menuId: string | number;
  pageLoaded?: any;
  sourceType: string;
}) {
  const [loading, setLoading] = useState(false);
  const [menuTab, setMenuTab] = useState([]);
  const [repeatListData, setRepeatListData] = useState<{ list?: Array<any> }>(
    {},
  );

  const requestData = () => {
    const key = idKeyNameMap[sourceType];

    if (!gameInfo.edition_unified_id || !menuId || !key) return;
    setLoading(true);
    getReportMenuTab(
      {
        [key]: Number(menuId),
        edition_unified_id: gameInfo.edition_unified_id,
        sources: ['linked', 'repeat_summary_task', "public_report_summary"],
        sort_item: 'create_time',
        sort_model: 'asc',
      },
      {
        rawData: true,
      },
    )
      .then(({ data, ext }: { data: any; ext: any }) => {
        data?.sort((a, b) => a.sort - b.sort);
        setMenuTab(data);
        setRepeatListData(ext?.repeat_summary_task || {});
      })
      .finally(() => {
        pageLoaded && (pageLoaded.current = true);
        setLoading(false);
      });
  };

  useEffect(() => {
    requestData();
  }, [gameInfo, menuId, sourceType]);

  return { loading, menuTab, repeatListData, setLoading, requestData };
}
